import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { PlatformSaleService } from 'src/app/services/platform-sale/platform-sale.service';
import { FavoriteService } from 'src/app/services/favorite/favorite.service';
import { BrowserStorageService } from 'src/app/services/browser-storage/browser-storage.service';
import { TRENDING_SELLER_GRADIENTS, TrendingSellerModel, TrendingSellerPage } from 'src/app/models/trending-seller.model';

interface TopSeller extends TrendingSellerModel {
  rank: number;
  initial: string;
  avatarGradient: string;
  isFollowBusy: boolean;
}

/**
 * Top Sellers (shared): sellers from the producer/trending-sellers API.
 *  - section (landing page): one scrolling row of cards and "View All" (opens /top-sellers); hidden when there are no sellers
 *  - page (isPage, /top-sellers): the sellers as tiles, 20 at a time (offset / limit), more load as the page scrolls
 * Cards open the producer profile; Follow follows / unfollows (logged-out users go to sign in and come back here).
 * Seller Listing Algorithm v3.0: the API's band order is kept, empty slots are not backfilled, the signed-in seller is
 * left out, and cards show the Pro / Premium subscription badge.
 * Usage: <ipm-top-sellers></ipm-top-sellers> or <ipm-top-sellers [isPage]="true"></ipm-top-sellers>
 */
@Component({
  selector: 'ipm-top-sellers',
  templateUrl: './top-sellers.component.html',
  styleUrls: ['./top-sellers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopSellersComponent implements OnInit, OnDestroy {
  /** Full page (/top-sellers) instead of the landing page row */
  @Input() isPage: boolean = false;

  sellers: Array<TopSeller> = [];
  isLoaded: boolean = false;
  /** Home page row: one band of 6 (the rest are on the See All page) */
  readonly landingCount: number = 6;
  /** Page: sellers per request (limit), the first request starts at offset 0 */
  readonly pageSize: number = 20;
  /** Page: more sellers can be loaded */
  hasMore: boolean = false;
  /** Page: the next pages are loading (the first page uses the page loader) */
  isLoadingMore: boolean = false;
  private nextOffset: number = 0;
  private destroy$ = new Subject<void>();

  constructor(
    public commonService: CommonService,
    private platformSaleService: PlatformSaleService,
    private favoriteService: FavoriteService,
    private browserStorageService: BrowserStorageService,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.isPage) {
      this.loadPage(0);
      return;
    }
    this.platformSaleService.getTrendingSellerList(false).pipe(takeUntil(this.destroy$)).subscribe({
      next: (sellerList: Array<TrendingSellerModel>) => {
        this.sellers = this.toCards(sellerList, 0);
        this.isLoaded = true;
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.isLoaded = true;
        this.changeDetector.markForCheck();
      },
    });
  }

  /** Page: load the sellers from `offset` (limit = pageSize) and add them to the tiles */
  private loadPage(offset: number): void {
    const isFirstPage = offset === 0;
    this.isLoadingMore = !isFirstPage;
    this.platformSaleService.getTrendingSellerPage(offset, this.pageSize, isFirstPage).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page: TrendingSellerPage) => {
        // a seller already on the page is not added twice
        const known = new Set(this.sellers.map((seller: TopSeller) => seller.id));
        const fresh = page.sellers.filter((seller: TrendingSellerModel) => !known.has(seller.id));
        this.sellers = [...this.sellers, ...this.toCards(fresh, this.sellers.length)];
        this.nextOffset = offset + this.pageSize;
        // a later page with nothing new means the list is over (the API gave the same sellers again)
        this.hasMore = page.hasMore && (isFirstPage || fresh.length > 0);
        this.isLoaded = true;
        this.isLoadingMore = false;
        this.changeDetector.markForCheck();
      },
      error: () => {
        // the next scroll tries this page again
        this.isLoaded = true;
        this.isLoadingMore = false;
        this.changeDetector.markForCheck();
      },
    });
  }

  /** Page: the end of the tiles was reached, load the next page */
  onScroll(): void {
    if (this.isPage && this.hasMore && !this.isLoadingMore) {
      this.loadPage(this.nextOffset);
    }
  }

  /** API sellers -> cards: a signed-in seller does not see themselves (the slot is not backfilled); `start` is the position of the first card */
  private toCards(sellerList: Array<TrendingSellerModel>, start: number): Array<TopSeller> {
    const userId = this.browserStorageService.getSessionData() ? this.browserStorageService.getUserId() : '';
    return sellerList
      .filter((seller: TrendingSellerModel) => !userId || seller.id !== userId)
      .map((seller: TrendingSellerModel, index: number) => ({
        ...seller,
        rank: start + index + 1,
        initial: seller.name.trim().charAt(0).toUpperCase(),
        avatarGradient: TRENDING_SELLER_GRADIENTS[(start + index) % TRENDING_SELLER_GRADIENTS.length],
        isFollowBusy: false,
      }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackById(index: number, seller: TopSeller): string {
    return seller.id;
  }

  openProfile(seller: TopSeller): void {
    if (seller.handle) {
      this.commonService.goToArtistProfile(seller.handle);
    }
  }

  /** Follow / unfollow (logged-out users get the login popup) */
  toggleFollow(seller: TopSeller, event: Event): void {
    event.stopPropagation();
    if (!this.browserStorageService.getSessionData()) {
      // sign in page (as the navbar), then back to this page; set after navigating (app.component overwrites it on NavigationEnd)
      const returnUrl = this.router.url;
      this.router.navigate(['/signin']).then(() => this.browserStorageService.setPreviousRoute(returnUrl));
      return;
    }
    if (seller.isFollowBusy) {
      return;
    }
    seller.isFollowBusy = true;
    const request = seller.isFollowed ? this.favoriteService.unFollowProducer(seller.id) : this.favoriteService.followProducer(seller.id);
    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        seller.isFollowed = !seller.isFollowed;
        if (seller.followers !== null) {
          seller.followers = Math.max(0, seller.followers + (seller.isFollowed ? 1 : -1));
        }
        seller.isFollowBusy = false;
        this.changeDetector.markForCheck();
      },
      error: (error: any) => {
        seller.isFollowBusy = false;
        this.commonService.showToastError(error);
        this.changeDetector.markForCheck();
      },
    });
  }
}
